import { BaseApiService } from './baseApiService';
import { handleApiError } from './apiClient';

/**
 * Modern User Service
 * 
 * This service handles all user-related API operations using modern REST endpoints.
 * Extends BaseApiService for common functionality.
 */

class UserService extends BaseApiService {
    constructor() {
        super('/users');

        // Field mapping for legacy compatibility
        this.fieldMap = {
            first_name: 'firstName',
            last_name: 'lastName',
            is_active: 'isActive',
            phone: 'mobileNumber'
        };

        // User list cache for staff assignment
        this.usersCache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * Clear the users cache
     * Call this when users are created, updated, or roles change
     */
    clearUsersCache() {
        this.usersCache.clear();
    }

    /**
     * Get users for staff assignment (cached, lightweight)
     * Optimized for dropdown/selection UIs with caching
     * @param {Object} params - Query parameters
     * @param {number} params.timeout - Optional custom timeout in milliseconds
     * @returns {Promise<Object>} - Cached users list
     */
    async getUsersForAssignment(params = {}) {
        const cacheKey = `users_assignment_${params.is_active !== false ? 'active' : 'all'}`;
        const cached = this.usersCache.get(cacheKey);

        // Return cached data if still valid
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            console.log('Using cached users for assignment');
            return cached.data;
        }

        // Fetch fresh data
        console.log('Fetching fresh users for assignment (lightweight mode)');
        const apiParams = {
            page: 1,
            page_size: 2000, // Increased from 100 to support larger organizations
            is_active: params.is_active !== false ? true : undefined,
            sort_by: 'name',
            sort_order: 'asc',
            include_rbac: false // Skip RBAC queries for 10x faster response
        };

        try {
            let response;

            // If custom timeout is specified, make the API call with custom config
            if (params.timeout) {
                const apiClient = (await import('./apiClient')).default;
                const apiResponse = await apiClient.get(this.baseUrl, {
                    params: apiParams,
                    timeout: params.timeout
                });

                const responseData = apiResponse.data;

                // Handle the response similar to BaseApiService.list()
                if (responseData?.success && responseData?.data) {
                    response = {
                        success: true,
                        data: responseData.data,
                        message: responseData.message
                    };
                } else {
                    response = {
                        success: true,
                        data: responseData
                    };
                }
            } else {
                response = await this.list(apiParams);
            }

            // Cache the response
            this.usersCache.set(cacheKey, {
                data: response,
                timestamp: Date.now()
            });

            return response;
        } catch (error) {
            console.error('Error fetching users for assignment:', error);
            // If fetch fails but we have expired cache, return it
            if (cached) {
                console.warn('Using expired cache due to fetch error:', error);
                return cached.data;
            }
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || 'Failed to retrieve users'
            };
        }
    }

    // ============================================================================
    // USER CRUD OPERATIONS (Simplified using BaseApiService)
    // ============================================================================

    /**
     * Get users with filtering and pagination
     * @param {Object} params - Query parameters
     * @param {number} params.timeout - Optional custom timeout in milliseconds
     * @returns {Promise<Object>} - Users list with pagination
     */
    async getUsers(params = {}) {
        const apiParams = {
            page: params.page || 1,
            page_size: params.page_size || params.per_page || 20,
            search: params.search || params.name,
            role: params.role,
            department: params.department,
            is_active: params.is_active !== undefined
                ? params.is_active
                : (params.status === 'active' ? true : (params.status === 'inactive' ? false : null)),

            ...(params.fields ? { fields: Array.isArray(params.fields) ? params.fields.join(',') : params.fields } : {})
        };

        // If custom timeout is specified, make the API call with custom config
        if (params.timeout) {
            try {
                const apiClient = (await import('./apiClient')).default;
                const response = await apiClient.get(this.baseUrl, {
                    params: apiParams,
                    timeout: params.timeout
                });

                const responseData = response.data;

                // Handle the response similar to BaseApiService.list()
                if (responseData?.success && responseData?.data) {
                    return {
                        success: true,
                        data: responseData.data,
                        message: responseData.message
                    };
                }

                return {
                    success: true,
                    data: responseData
                };
            } catch (error) {
                console.error('Error getting users with custom timeout:', error);
                return {
                    success: false,
                    data: [],
                    message: error.response?.data?.detail || 'Failed to retrieve users'
                };
            }
        }

        return this.list(apiParams);
    }

    /**
     * Get single user by ID
     * @param {string} userId - User ID
     * @returns {Promise<Object>} User response
     */
    async getUserById(userId) {
        return this.getById(userId);
    }

    /**
     * Create new user
     * @param {Object} userData - User data in legacy format
     * @returns {Promise<Object>} User response
     */
    async createUser(userData) {
        try {
            // Validate required fields
            this.validateRequiredFields(userData, ['email', 'password']);

            // Extract roles for separate handling
            const roles = userData.roles;
            console.log('UserService.createUser - Input userData.roles:', roles);
            console.log('UserService.createUser - Roles type:', Array.isArray(roles) ? 'array' : typeof roles);
            console.log('UserService.createUser - Roles length:', roles ? roles.length : 'N/A');

            // Generate username from email if not provided
            const username = userData.username || userData.email.split('@')[0];

            // Transform legacy fields to API format
            const apiData = this.transformLegacyFields(userData, this.fieldMap);

            // Ensure proper field names for API
            const formattedData = {
                username: username,
                email: userData.email,
                password: userData.password,
                first_name: apiData.first_name || userData.firstName || userData.first_name,
                last_name: apiData.last_name || userData.lastName || userData.last_name,
                middle_name: userData.middle_name,
                name_prefix: userData.name_prefix,
                role: userData.role || 'staff',
                department: userData.department,
                specialty: userData.specialty,
                phone: userData.phone || userData.mobileNumber,
                address: userData.address,
                city: userData.city,
                state: userData.state,
                designation: userData.designation,
                employee_id: userData.employee_id,
                emirates_id_number: userData.emirates_id_number,
                license_number: userData.license_number,
                date_of_joining: userData.date_of_joining,
                releavingDate: userData.releavingDate,
                gender: userData.gender,
                roles: roles || [], // Include roles in main payload
                is_active: userData.isActive !== undefined ? userData.isActive : true
            };

            console.log('UserService.createUser - Final formattedData.roles:', formattedData.roles);
            console.log('UserService.createUser - Sending data:', formattedData);

            // Create user
            const cleanedData = this.removeUndefinedValues(formattedData);
            console.log('UserService.createUser - After removeUndefinedValues, roles:', cleanedData.roles);
            const response = await this.create(cleanedData);

            // Handle roles via RBAC if roles are provided
            if (roles && Array.isArray(roles) && roles.length > 0 && response.success && response.data) {
                try {
                    // Import RBAC service
                    const { default: rbacService } = await import('./rbacService');

                    // Assign roles to the new user
                    const userId = response.data.id;
                    await rbacService.updateUserRoles(userId, roles);

                    console.log('UserService.createUser - Roles assigned:', roles);
                } catch (roleError) {
                    console.warn('Failed to assign user roles:', roleError);
                    // Don't fail the entire creation if role assignment fails
                }
            }

            console.log('UserService.createUser - Response:', response);

            return response;
        } catch (error) {
            console.error('Error in UserService.createUser:', error);
            throw error;
        }
    }

    /**
     * Update existing user
     * @param {string} userId - User ID
     * @param {Object} userData - Updated user data in legacy format
     * @returns {Promise<Object>} User response
     */
    async updateUser(userId, userData) {
        try {
            // Extract roles for separate handling
            const roles = userData.roles;

            // Transform legacy fields to API format (excluding roles)
            const apiData = {
                email: userData.email,
                first_name: userData.firstName || userData.first_name,
                last_name: userData.lastName || userData.last_name,
                middle_name: userData.middle_name,
                name_prefix: userData.name_prefix,
                department: userData.department,
                specialty: userData.specialty,
                phone: String(userData.phone || userData.mobileNumber || ""),
                address: userData.address,
                city: userData.city,
                state: userData.state,
                designation: userData.designation,
                employee_id: userData.employee_id,
                emirates_id_number: userData.emirates_id_number,
                license_number: userData.license_number,
                date_of_joining: userData.date_of_joining,
                releavingDate: userData.releavingDate,
                gender: userData.gender,
                role: userData.role,
                is_active: userData.is_active !== undefined ? userData.is_active : userData.isActive
            };

            // Remove undefined values
            const cleanedData = this.removeUndefinedValues(apiData);

            console.log('UserService.updateUser - Sending data:', cleanedData);

            // Update user basic information
            const response = await this.update(userId, cleanedData);

            // Handle roles via RBAC if roles are provided
            if (roles && Array.isArray(roles) && roles.length > 0) {
                try {
                    // Import RBAC service
                    const { default: rbacService } = await import('./rbacService');

                    // Remove existing roles and assign new ones
                    await rbacService.updateUserRoles(userId, roles);

                    console.log('UserService.updateUser - Roles updated:', roles);
                } catch (roleError) {
                    console.warn('Failed to update user roles:', roleError);
                    // Don't fail the entire update if role assignment fails
                }
            }

            console.log('UserService.updateUser - Response:', response);

            return response;
        } catch (error) {
            console.error('Error in UserService.updateUser:', error);
            throw error;
        }
    }

    /**
     * Update staff profile using dedicated staff profile endpoint
     * @param {string} userId - User ID
     * @param {Object} userData - Updated user data in legacy format
     * @param {Object} originalData - Original user data to compare for changes (optional)
     * @returns {Promise<Object>} User response
     */
    async updateStaffProfile(userId, userData, originalData = null) {
        try {
            // Extract roles for separate handling
            const roles = userData.roles;
            const originalRoles = originalData?.roles;

            // Transform legacy fields to API format (excluding roles and role-related fields)
            const apiData = {
                email: userData.email,
                first_name: userData.firstName || userData.first_name,
                last_name: userData.lastName || userData.last_name,
                middle_name: userData.middle_name,
                name_prefix: userData.name_prefix,
                department: userData.department,
                specialty: userData.specialty,
                phone: String(userData.phone || userData.mobileNumber || ""),
                address: userData.address,
                city: userData.city,
                state: userData.state,
                designation: userData.designation,
                employee_id: userData.employee_id,
                emirates_id_number: userData.emirates_id_number,
                license_number: userData.license_number,
                date_of_joining: userData.date_of_joining,
                releavingDate: userData.releavingDate,
                gender: userData.gender,
                is_active: userData.is_active !== undefined ? userData.is_active : userData.isActive
                // Note: Excluding 'role' field as it should be managed through RBAC, not profile updates
            };

            // Remove undefined values
            const cleanedData = this.removeUndefinedValues(apiData);

            console.log('UserService.updateStaffProfile - Sending profile data (excluding roles):', cleanedData);

            // Import apiClient like other methods in BaseApiService
            const apiClient = (await import('./apiClient')).default;

            // Update staff profile using dedicated endpoint (excludes roles)
            const response = await apiClient.put(`${this.baseUrl}/${userId}/staff-profile`, cleanedData);

            // Only handle roles separately if they have actually changed
            if (roles && Array.isArray(roles)) {
                // Check if roles have actually changed by comparing role values
                const currentRoleValues = roles.map(role => typeof role === 'object' ? role.value : role).sort();
                const originalRoleValues = originalRoles ? 
                    originalRoles.map(role => typeof role === 'object' ? role.value : role).sort() : [];

                const rolesChanged = JSON.stringify(currentRoleValues) !== JSON.stringify(originalRoleValues);

                if (rolesChanged) {
                    try {
                        console.log('UserService.updateStaffProfile - Roles have changed, updating separately');
                        console.log('UserService.updateStaffProfile - Original roles:', originalRoleValues);
                        console.log('UserService.updateStaffProfile - New roles:', currentRoleValues);

                        // Import RBAC service
                        const { default: rbacService } = await import('./rbacService');

                        // Update user roles separately from profile
                        await rbacService.updateUserRoles(userId, currentRoleValues);

                        console.log('UserService.updateStaffProfile - Roles updated successfully');
                    } catch (roleError) {
                        console.warn('Failed to update user roles:', roleError);
                        // Don't fail the entire update if role assignment fails
                    }
                } else {
                    console.log('UserService.updateStaffProfile - Roles unchanged, skipping role update');
                }
            }

            const result = {
                success: true,
                data: response.data,
                message: "Staff profile updated successfully"
            };

            console.log('UserService.updateStaffProfile - Response:', result);

            return result;
        } catch (error) {
            console.error('Error in UserService.updateStaffProfile:', error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Delete user (soft delete)
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Success response
     */
    async deleteUser(userId) {
        return this.delete(userId);
    }

    /**
     * Bulk delete users
     * @param {Array<string>} userIds - Array of user IDs
     * @returns {Promise<Object>} Success response
     */
    async bulkDeleteUsers(userIds) {
        return this.bulk('delete', { user_ids: userIds });
    }

    // ============================================================================
    // USER STATUS MANAGEMENT
    // ============================================================================

    /**
     * Activate user
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Success response
     */
    async activateUser(userId) {
        return this.updateStatus(userId, 'active');
    }

    /**
     * Deactivate user
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Success response
     */
    async deactivateUser(userId) {
        return this.updateStatus(userId, 'inactive');
    }

    /**
     * Reset user password
     * @param {string} userId - User ID
     * @param {Object} passwordData - Password reset data
     * @returns {Promise<Object>} Success response
     */
    async resetUserPassword(userId, passwordData) {
        try {
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.post(`${this.baseUrl}/${userId}/reset-password`, passwordData);
            return {
                success: true,
                data: response.data,
                message: "Password reset successfully"
            };
        } catch (error) {
            console.error('Error resetting password:', error);
            throw this.handleApiError(error);
        }
    }

    // ============================================================================
    // CONVENIENCE METHODS (Leveraging BaseApiService)
    // ============================================================================

    /**
     * Get users with lightweight response for better performance
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - Lightweight users list
     */
    async getUsersLightweight(params = {}) {
        return this.listLightweight(params);
    }

    /**
     * Search users
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} - Search results
     */
    async searchUsers(searchParams) {
        return this.search(searchParams);
    }

    /**
     * Get multiple users by IDs using the optimized bulk endpoint
     * @param {Array} userIds - Array of user IDs
     * @returns {Promise<Object>} - Bulk user data
     */
    async getUsersBulk(userIds) {
        try {
            if (!Array.isArray(userIds) || userIds.length === 0) {
                return {
                    success: true,
                    data: [],
                    message: "No user IDs provided"
                };
            }

            // Import apiClient directly like other methods in BaseApiService
            const apiClient = (await import('./apiClient')).default;
            
            // Use the new optimized bulk endpoint
            const response = await apiClient.post(`${this.baseUrl}/bulk`, userIds);
            
            // Extract users from the response data structure: {success: true, data: {users: [...]}}
            const users = response.data?.data?.users || response.data?.users || [];
            
            return {
                success: true,
                data: users,
                message: "Bulk users fetched successfully"
            };
        } catch (error) {
            console.error('Error fetching bulk users:', error);
            return {
                success: false,
                data: null,
                message: error.message || "Failed to fetch bulk users"
            };
        }
    }

    /**
     * Get multiple users by IDs (legacy method)
     * @param {Array} userIds - Array of user IDs
     * @returns {Promise<Object>} - Batch user data
     */
    async getUsersBatch(userIds) {
        return this.getBatch(userIds);
    }

    // ============================================================================
    // USER PROFILE MANAGEMENT
    // ============================================================================

    /**
     * Get current user profile
     * @returns {Promise<Object>} Transformed user response
     */
    async getCurrentUserProfile() {
        try {
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.get(`${this.baseUrl}/profile`);
            return {
                success: true,
                data: response.data,
                message: "Profile fetched successfully"
            };
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Update current user profile
     * @param {Object} profileData - Profile data
     * @returns {Promise<Object>} Transformed user response
     */
    async updateCurrentUserProfile(profileData) {
        try {
            // Convert legacy field names to modern API format
            const apiData = {
                email: profileData.email,
                first_name: profileData.firstName || profileData.first_name,
                last_name: profileData.lastName || profileData.last_name,
                department: profileData.department
            };

            // Remove undefined values
            Object.keys(apiData).forEach(key =>
                apiData[key] === undefined && delete apiData[key]
            );

            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.put(`${this.baseUrl}/profile`, apiData);
            return {
                success: true,
                data: response.data,
                message: "Profile updated successfully"
            };
        } catch (error) {
            console.error('Error updating profile:', error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Change current user password
     * @param {Object} passwordData - Password change data
     * @returns {Promise<Object>} Success response
     */
    async changePassword(passwordData) {
        try {
            // Transform field names to match backend expectations
            const transformedData = {
                current_password: passwordData.currentPassword || passwordData.oldPassword,
                new_password: passwordData.newPassword,
                confirm_password: passwordData.confirmPassword || passwordData.newPassword // Fallback to newPassword if confirmPassword not provided
            };
            
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.post(`${this.baseUrl}/auth/change-password`, transformedData);
            return {
                success: true,
                data: response.data,
                message: "Password changed successfully"
            };
        } catch (error) {
            console.error('Error changing password:', error);
            throw this.handleApiError(error);
        }
    }

    // ============================================================================
    // USER SEARCH & FILTERING
    // ============================================================================

    /**
     * Search users with advanced filters
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} Transformed user list response
     */
    async searchUsers(searchParams) {
        try {
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.get(`${this.baseUrl}/search`, { params: searchParams });
            return response.data;
        } catch (error) {
            console.error('Error searching users:', error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Get users by role
     * @param {string} roleId - Role ID
     * @param {Object} params - Additional query parameters
     * @returns {Promise<Object>} Transformed user list response
     */
    async getUsersByRole(roleId, params = {}) {
        try {
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.get(this.baseUrl, { params: { ...params, role_id: roleId } });
            return response.data;
        } catch (error) {
            console.error('Error fetching users by role:', error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Get users by department
     * @param {string} departmentId - Department ID
     * @param {Object} params - Additional query parameters
     * @returns {Promise<Object>} Transformed user list response
     */
    async getUsersByDepartment(departmentId, params = {}) {
        try {
            const apiClient = (await import('./apiClient')).default;
            const response = await apiClient.get(this.baseUrl, { params: { ...params, department_id: departmentId } });
            return response.data;
        } catch (error) {
            console.error('Error fetching users by department:', error);
            throw this.handleApiError(error);
        }
    }

    // ============================================================================
    // LEGACY COMPATIBILITY METHODS
    // ============================================================================

    /**
     * Legacy method: Get user list (maintains backward compatibility)
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getUserListAction(params = {}) {
        return this.getUsers(params);
    }

    /**
     * Legacy method: Save user (create or update)
     * @param {Object} userData - User data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async saveUserAction(userData) {
        if (userData.id) {
            return this.updateUser(userData.id, userData);
        } else {
            return this.createUser(userData);
        }
    }

    /**
     * Legacy method: Delete user
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async deleteUserAction(userId) {
        return this.deleteUser(userId);
    }

    /**
     * Legacy method: Get user details
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getUserDetailsAction(userId) {
        return this.getUserById(userId);
    }
}

// Export singleton instance
export default new UserService();